import React, { Dispatch, FC, MutableRefObject, SetStateAction, useEffect, useRef, useState } from 'react';
import {
  Address,
  Customer, CustomerTypes,
  getAddressDisplayName,
  Lead,
  MinimalUser, Note,
  Operation, OperationHistoryAddLead,
  OperationHistoryDownloadDocument,
  OperationHistoryPageChange,
  OperationHistorySelectCustomer, OperationHistorySendDocument,
  OperationHistoryStartOperation, OperationHistoryViewDocument,
  OperationHistoryViewProduct,
  OperationHistoryViewResultSet,
  OperationStatus,
  Product, ProductCategory, QuestionnaireWithResultSets,
  UserProductPermission,
} from '../../..';
import {
  QuestionnaireAnswers,
} from './QuestionnaireFormBuilder';
import { AdditionalQuestionsAnswers } from './static-pages/QuestionnaireBaseQuestions';
import { useRecoilState } from 'recoil';
import { NotesState } from '../../../state/NotesState';
import * as AuthStore from '../../store/AuthStore';
import { OperationHistoryItemProp } from '../../../interfaces/Operation';
import { Attachment } from '../../../interfaces/Attachment';
import { WizardItemProps } from '../Wizard';
import { useMultipleAttachments } from '../../../utils/Attachments';

type SetStateType<T> = Dispatch<SetStateAction<T>>;
type StateType<T> = [T, SetStateType<T>];
type RefType<T> = MutableRefObject<T>;

interface ContextState {
  questionnairePageVisible: RefType<boolean>;

  currentPage: number;
  setCurrentPage: SetStateType<number>;
  showErrorStates: boolean;
  setShowErrorStates: SetStateType<boolean>;
  selectedCustomerIdRef: RefType<string | null>;
  selectedCustomerState: StateType<Customer | null>;
  openProductCategories: string[];
  setOpenProductCategories: SetStateType<string[]>;
  selectedProduct: Product | null;
  setSelectedProduct: SetStateType<Product | null>;
  selectedProductAttachments: Attachment[];
  concludingNotes: string;
  setConcludingNotes: SetStateType<string>;
  skipProductSelection: RefType<boolean>;

  hasLeadPermission: boolean;
  hasSampleContractPermission: boolean;
  hasContractPermission: boolean;

  baseQuestionsState: StateType<QuestionnaireAnswers>;
  baseQuestionsAnswered: string[];
  setBaseQuestionsAnswered: SetStateType<string[]>;
  baseQuestionsResultSets: string[];
  setBaseQuestionsResultSets: SetStateType<string[]>;
  additionalQuestionsState: StateType<AdditionalQuestionsAnswers>;
  additionalQuestionsAnswered: { [key: string]: string[] };
  setAdditionalQuestionsAnswered: SetStateType<{ [key: string]: string[] }>;
  additionalQuestionsResultSets: { [key: string]: string[] };
  setAdditionalQuestionsResultSets: SetStateType<{ [key: string]: string[] }>;
  openAccordions: string[];
  setOpenAccordions: SetStateType<string[]>;
  openProductCategoriesPerAddress: { [key: string]: string[] };
  setOpenProductCategoriesPerAddress: SetStateType<{ [key: string]: string[] }>;
  additionalAddressSelected: RefType<string | null>;

  leadQuestionsState: StateType<{ [questionnaireId: string]: QuestionnaireAnswers }>;
  leadQuestionsAnswered: string[];
  setLeadQuestionsAnswered: SetStateType<string[]>;
  selectedUserLeadState: StateType<MinimalUser | null>;
  notes: { list: Note[] };
  lead: Lead | null;
  setLead: SetStateType<Lead | null>;
  leadPageType: RefType<'LEAD' | 'SAMPLE_CONTRACT' | 'CONTRACT' | 'PRODUCT_INFO'>;

  concludingQuestionsState: StateType<QuestionnaireAnswers>;
  concludingQuestionsAnswered: string[];
  setConcludingQuestionsAnswered: SetStateType<string[]>;

  operation: Operation | null;
  setOperation: SetStateType<Operation | null>;
  tmpOperation: Operation | null;
  setTmpOperation: SetStateType<Operation | null>;
  getDefaultOperation: () => Operation;
  logAction: (action: OperationHistoryItemProp, operationBase?: Operation | null) => Operation;

  wizardData: WizardItemProps[];
  customer: Customer | null;
  questionnaireBase: QuestionnaireWithResultSets;
  allQuestionnairesLeads: QuestionnaireWithResultSets[];
  questionnaireConcludingQuestions: QuestionnaireWithResultSets;

  productsList: ProductCategory[];
  productsListPerAddress: ({ productsList: ProductCategory[] } & Address)[];

  onDocumentActionListener: (type: 'view' | 'download' | 'send', attachment: Attachment, receiver?: string) => void;

  loadStateFromOperation: (operation: Operation | null) => void;
}

export const QuestionnaireContext = React.createContext<Partial<ContextState>>({});

interface Props {
  customers: Customer[] | null;
  productCategories: ProductCategory[] | null;
  questionnaires: QuestionnaireWithResultSets[] | null;
  permissions: UserProductPermission[];
}
export const QuestionnaireContextProvider: FC<Props> = ({ permissions, ...props }) => {
  const questionnairePageVisible = useRef(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [showErrorStates, setShowErrorStates] = useState(false);
  const selectedCustomerIdRef = useRef<string | null>(null);
  const selectedCustomerState = useState<Customer | null>(null);
  const [openProductCategories, setOpenProductCategories] = useState<string[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const selectedProductAttachments = useMultipleAttachments(selectedProduct?.attachments || []);
  const [concludingNotes, setConcludingNotes] = useState<string>('');
  const skipProductSelection = useRef(false);

  const hasLeadPermission: boolean = permissions.find(({ productId }) => productId === selectedProduct?.id)?.hasLeadPermission || false;
  const hasSampleContractPermission: boolean = permissions.find(({ productId }) => productId === selectedProduct?.id)?.hasSampleContractPermission || false;
  const hasContractPermission: boolean = permissions.find(({ productId }) => productId === selectedProduct?.id)?.hasContractPermission || false;

  const baseQuestionsState = useState<QuestionnaireAnswers>({});
  const [baseQuestionsAnswered, setBaseQuestionsAnswered] = useState<string[]>([]);
  const [baseQuestionsResultSets, setBaseQuestionsResultSets] = useState<string[]>([]);
  const additionalQuestionsState = useState<AdditionalQuestionsAnswers>({});
  const [additionalQuestionsAnswered, setAdditionalQuestionsAnswered] = useState<{ [key: string]: string[] }>({});
  const [additionalQuestionsResultSets, setAdditionalQuestionsResultSets] = useState<{ [key: string]: string[] }>({});
  const [openAccordions, setOpenAccordions] = useState<string[]>([]);
  const [openProductCategoriesPerAddress, setOpenProductCategoriesPerAddress] = useState<{ [key: string]: string[] }>({});
  const additionalAddressSelected = useRef<string | null>(null);

  const leadQuestionsState = useState<{ [questionnaireId: string]: QuestionnaireAnswers }>({});
  const [leadQuestionsAnswered, setLeadQuestionsAnswered] = useState<string[]>([]);
  const selectedUserLeadState = useState<MinimalUser | null>(null);
  const [notes, setNotes] = useRecoilState(NotesState);
  const [lead, setLead] = useState<Lead | null>(null);
  const leadPageType = useRef<'LEAD' | 'SAMPLE_CONTRACT' | 'CONTRACT' | 'PRODUCT_INFO'>('LEAD');

  const concludingQuestionsState = useState<QuestionnaireAnswers>({});
  const [concludingQuestionsAnswered, setConcludingQuestionsAnswered] = useState<string[]>([]);

  const [operation, setOperation] = useState<Operation | null>(null);
  const [tmpOperation, setTmpOperation] = useState<Operation | null>(null);
  const getDefaultOperation: () => Operation = () => ({
    createdAt: new Date(),
    updatedAt: new Date(),
    customerId: selectedCustomerState[0]?.id || '',
    userId: AuthStore.getCurrentUserId(),
    status: OperationStatus.CREATED,
    questions: [],
    concludingQuestions: [],
    history: [
      {
        type: 'START_OPERATION',
        timestamp: new Date().toISOString(),
      } as OperationHistoryStartOperation,
    ],
    notes: [],
  });
  const logAction = (action: OperationHistoryItemProp, operationBase: Operation | null = tmpOperation) => {
    const base = operationBase || getDefaultOperation();
    const nextOperation = {
      ...base,
      customerId: selectedCustomerState[0]?.id || '',
      history: [
        ...(base?.history || []),
        {
          ...(additionalAddressSelected.current && { additionalAddress: additionalAddressSelected.current } || {}),
          ...action,
          timestamp: new Date().toISOString(),
        },
      ],
    } as Operation;
    setTmpOperation(nextOperation);
    return nextOperation;
  };

  useEffect(() => {
    if (currentPage < 1) {
      return;
    }

    // log page change
    let nextOperation = logAction({
      type: 'PAGE_CHANGE',
      pageTitle: wizardData[currentPage].title,
    } as OperationHistoryPageChange);

    if (currentPage === 2) {
      nextOperation = logAction({
        type: 'VIEW_RESULT_SET',
        products: productsList?.flatMap(({ products }) => products.map(({ name }) => name)) || [],
      } as OperationHistoryViewResultSet, nextOperation);

      // log result sets for additional addresses
      (productsListPerAddress || []).map(({ productsList, ...address }) => {
        nextOperation = logAction({
          type: 'VIEW_RESULT_SET',
          products: productsList?.flatMap(({ products }) => products.map(({ name }) => name)) || [],
          additionalAddress: getAddressDisplayName(address),
        } as OperationHistoryViewResultSet, nextOperation);
      });
    }

    if (currentPage === 3) {
      nextOperation = logAction({
        type: 'VIEW_PRODUCT',
        product: selectedProduct,
      } as OperationHistoryViewProduct, nextOperation);
    }

    // reset additional address's accordion open states
    setOpenAccordions([]);
  }, [currentPage]);

  useEffect(() => {
    if (selectedCustomerState[0] && (!selectedCustomerIdRef.current || selectedCustomerIdRef.current !== selectedCustomerState[0].id)) {
      selectedCustomerIdRef.current = selectedCustomerState[0].id;
      logAction({
        type: 'SELECT_CUSTOMER',
        customer: selectedCustomerState[0],
      } as OperationHistorySelectCustomer);
    }
  }, [selectedCustomerState[0]]);

  useEffect(() => console.log('operation builder', tmpOperation), [tmpOperation]);

  const wizardData = [
    {
      title: 'Daten eingeben',
      numTitle: 1,
      active: true,
      current: currentPage === 0,
    },
    {
      title: 'Basisfragen',
      numTitle: 2,
      active: currentPage > 0,
      current: currentPage === 1,
    },
    {
      title: 'Lösung auswählen',
      numTitle: 3,
      active: currentPage > 1,
      current: currentPage === 2,
    },
    {
      title: 'Produktansicht',
      numTitle: 4,
      active: currentPage > 2,
      current: currentPage === 3,
    },
    {
      title: (
        leadPageType.current === 'CONTRACT' && hasContractPermission ? 'Vertrag'
          : leadPageType.current === 'SAMPLE_CONTRACT' && hasSampleContractPermission ? 'Mustervertrag'
          : leadPageType.current === 'LEAD' && hasLeadPermission ? 'Lead generieren'
            : leadPageType.current === 'PRODUCT_INFO' ? 'Produktsteckbrief'
              : ''
      ),
      numTitle: 5,
      active: currentPage > 3,
      current: currentPage === 4,
    },
    {
      title: 'Abschluss',
      numTitle: 6,
      active: currentPage > 4,
      current: currentPage === 5,
    },
  ];

  const customer = selectedCustomerState[0];
  const questionnaireBase = props.questionnaires?.find((item) => item.questionnaire?.type === customer?.type);
  const allQuestionnairesLeads = props.questionnaires?.filter((item) => (
    (selectedProduct?.leadQuestionnaires || []).includes(item.questionnaire?.type)
  ));
  const questionnaireConcludingQuestions: QuestionnaireWithResultSets = props.questionnaires?.find((item) => item.questionnaire?.type === 'END');

  const filterProductsList = (fun: (product: Product, customerTypeKey: string) => boolean): ProductCategory[] | undefined => {
    return props.productCategories && props.productCategories.map((category) => {
      const products = category.products?.filter((product) => {
        const key = (() => {
          switch (customer?.type || CustomerTypes.BUSINESS) {
            case CustomerTypes.MAJOR_CUSTOMERS:
              return 'majorCustomers';
            case CustomerTypes.HOUSING_INDUSTRY:
              return 'housingIndustry';
            default:
            case CustomerTypes.BUSINESS:
              return 'business';
          }
        })();

        return fun(product, key);
      });

      return {
        ...category,
        products,
      };
    }).filter((category) => category.products.length > 0);
  };

  // filter products
  const productsList = filterProductsList((product, key) => {
    return baseQuestionsResultSets.length > 0 &&
      baseQuestionsResultSets.every((resultSetId) => (product.resultSets || {})[key]?.includes(resultSetId));
  });

  // filter products per address
  const productsListPerAddress = (customer?.additionalAddresses || []).map((address, index) => ({
    ...address,
    productsList: filterProductsList((product, key) => {
      return additionalQuestionsResultSets[`${index}`]?.length > 0 &&
        additionalQuestionsResultSets[`${index}`].every((resultSetId) => (product.resultSets || {})[key]?.includes(resultSetId));
    }),
  }));

  // document listener
  const onDocumentActionListener = (type: 'view' | 'download' | 'send', attachment: Attachment, receiver?: string) => {
    switch (type) {
      case 'view':
        logAction({
          type: 'VIEW_DOCUMENT',
          documentId: attachment?.id,
          documentName: attachment?.fileName,
        } as OperationHistoryViewDocument);
        break;
      case 'download':
        logAction({
          type: 'DOWNLOAD_DOCUMENT',
          documentId: attachment?.id,
          documentName: attachment?.fileName,
        } as OperationHistoryDownloadDocument);
        break;
      case 'send':
        logAction({
          type: 'SEND_DOCUMENT',
          documentId: attachment?.id,
          documentName: attachment?.fileName,
        } as OperationHistorySendDocument);
        break;
    }
  };

  const loadStateFromOperation = (operation: Operation | null) => {
    if (operation) {
      setCurrentPage(1);
      setShowErrorStates(false);
      selectedCustomerIdRef.current = operation?.customerId || null;
      selectedCustomerState[1]((operation?.history?.slice().reverse().find(({ type }) => type === 'SELECT_CUSTOMER') as OperationHistorySelectCustomer)?.customer || null);
      setOpenProductCategories([]);
      setSelectedProduct((operation?.history?.slice().reverse().find(({ type }) => type === 'VIEW_PRODUCT') as OperationHistoryViewProduct)?.product || null);
      setConcludingNotes(operation?.notes?.find(({ subject }) => subject === 'Zusätzliche Anmerkungen')?.body || '');
      skipProductSelection.current = true;

      baseQuestionsState[1](operation?.questions?.reduce((res, item) => ({ ...res, [item.id]: item.value }), {}) || {});
      setBaseQuestionsAnswered(operation?.questions?.filter(({ answered }) => answered).map(({ id }) => id) || []);
      setBaseQuestionsResultSets([]);
      additionalQuestionsState[1](
        operation?.additionalQuestions && Object.keys(operation?.additionalQuestions).reduce((res, key) => ({
          ...res,
          [key]: operation?.additionalQuestions[key].reduce((res, item) => ({ ...res, [item.id]: item.value }), {}) || {},
        }), {}) || {},
      );
      setAdditionalQuestionsAnswered(
        operation?.additionalQuestions && Object.keys(operation?.additionalQuestions).reduce((res, key) => ({
          ...res,
          [key]: operation?.additionalQuestions[key].filter(({ answered }) => answered).map(({ id }) => id) || [],
        }), {}) || {});
      setAdditionalQuestionsResultSets({});
      setOpenAccordions([]);
      setOpenProductCategoriesPerAddress({});
      additionalAddressSelected.current = (operation?.history?.slice().reverse().find(({ type }) => type === 'VIEW_PRODUCT') as OperationHistoryViewProduct)?.additionalAddress || null;

      leadQuestionsState[1]({});
      setLeadQuestionsAnswered([]);
      selectedUserLeadState[1](null);
      setLead(null);
      leadPageType.current = 'LEAD';

      concludingQuestionsState[1](operation?.concludingQuestions?.reduce((res, item) => ({
        ...res,
        [item.id]: item.value,
      }), {}) || {});
      setConcludingQuestionsAnswered(operation?.concludingQuestions?.filter(({ answered }) => answered).map(({ id }) => id) || []);

      setOperation(operation || null);
      setTmpOperation(operation || null);

      // set notes state
      setNotes({
        list: (operation?.notes || []).filter(({ subject }) => subject !== 'Zusätzliche Anmerkungen'),
      });
    } else {
      resetAll();
    }
  };
  const resetAll = () => {
    setCurrentPage(0);
    setShowErrorStates(false);
    selectedCustomerIdRef.current = null;
    selectedCustomerState[1](null);
    setOpenProductCategories([]);
    setSelectedProduct(null);
    setConcludingNotes('');
    skipProductSelection.current = false;

    baseQuestionsState[1]({});
    setBaseQuestionsAnswered([]);
    setBaseQuestionsResultSets([]);
    additionalQuestionsState[1]({});
    setAdditionalQuestionsAnswered({});
    setAdditionalQuestionsResultSets({});
    setOpenAccordions([]);
    setOpenProductCategoriesPerAddress({});
    additionalAddressSelected.current = null;

    leadQuestionsState[1]({});
    setLeadQuestionsAnswered([]);
    selectedUserLeadState[1](null);
    setLead(null);
    leadPageType.current = 'LEAD';

    concludingQuestionsState[1]({});
    setConcludingQuestionsAnswered([]);

    setOperation(null);
    setTmpOperation(null);

    setNotes({ list: [] });
  };

  const context: ContextState = {
    questionnairePageVisible,

    currentPage, setCurrentPage,
    showErrorStates, setShowErrorStates,
    selectedCustomerIdRef,
    selectedCustomerState,
    openProductCategories, setOpenProductCategories,
    selectedProduct, setSelectedProduct,
    selectedProductAttachments,
    concludingNotes, setConcludingNotes,
    skipProductSelection,

    hasLeadPermission,
    hasSampleContractPermission,
    hasContractPermission,

    baseQuestionsState,
    baseQuestionsAnswered, setBaseQuestionsAnswered,
    baseQuestionsResultSets, setBaseQuestionsResultSets,
    additionalQuestionsState,
    additionalQuestionsAnswered, setAdditionalQuestionsAnswered,
    additionalQuestionsResultSets, setAdditionalQuestionsResultSets,
    openAccordions, setOpenAccordions,
    openProductCategoriesPerAddress, setOpenProductCategoriesPerAddress,
    additionalAddressSelected,

    leadQuestionsState,
    leadQuestionsAnswered, setLeadQuestionsAnswered,
    selectedUserLeadState,
    notes,
    lead, setLead,
    leadPageType,

    concludingQuestionsState,
    concludingQuestionsAnswered, setConcludingQuestionsAnswered,

    operation, setOperation,
    tmpOperation, setTmpOperation,
    getDefaultOperation,
    logAction,

    wizardData,
    customer,
    questionnaireBase,
    allQuestionnairesLeads,
    questionnaireConcludingQuestions,

    productsList,
    productsListPerAddress,

    onDocumentActionListener,

    loadStateFromOperation,
  };
  return (
    <QuestionnaireContext.Provider value={context}>
      {
        props.children
      }
    </QuestionnaireContext.Provider>
  );
};
